import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ENTREPRENEURS_LAYER_POLL_MS,
  AGRITOUR_ENTREPRENEURS_LAYER_POLL_MS,
  HOTELS_LAYER_POLL_MS,
} from '@/screens/TourismMap/layer-options';

export interface MockCrm {
  manager: string;
  phone: string;
  email: string;
  status: 'active' | 'lead' | 'inactive';
  rooms: number;
  stars: number;
  notes: string;
}

export interface MapFeatureGeometry {
  lat: number;
  lng: number;
  shape?: {
    type: 'Polygon';
    coordinates: number[][][];
  };
}

export interface EnrichedFeature {
  id: string | number;
  name: string;
  geometry: MapFeatureGeometry | null;
  attributes: Record<string, unknown>;
  crm: MockCrm;
  layerId: string;
  layerDisplayName: string;
}

interface UseGeoLayerResult {
  features: EnrichedFeature[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Used when the backend is unreachable in local dev (import.meta.env.DEV).
const DEV_MOCK_FEATURES: EnrichedFeature[] = [
  { id: 1, name: 'מלון גליל עליון', geometry: { lat: 33.0456, lng: 35.6112 }, attributes: { source: 'dev-mock' }, crm: { manager: 'דני לוי', phone: '052-1234567', email: 'dani@hotel.co.il', status: 'active', rooms: 45, stars: 4, notes: 'לקוח ותיק, עניין בהרחבה' }, layerId: 'hotels', layerDisplayName: 'מלונות' },
  { id: 2, name: 'ריזורט כינרת', geometry: { lat: 32.8245, lng: 35.5423 }, attributes: { source: 'dev-mock' }, crm: { manager: 'מיכל כהן', phone: '054-9876543', email: 'michal@resort.co.il', status: 'active', rooms: 80, stars: 5, notes: 'פרויקט הרחבה עצור' }, layerId: 'hotels', layerDisplayName: 'מלונות' },
  { id: 3, name: 'בוטיק צפת', geometry: { lat: 32.9641, lng: 35.4978 }, attributes: { source: 'dev-mock' }, crm: { manager: 'אבי ישראלי', phone: '050-1112233', email: 'avi@boutique.co.il', status: 'lead', rooms: 12, stars: 3, notes: 'יזם חדש, פגישה ביולי' }, layerId: 'hotels', layerDisplayName: 'מלונות' },
  { id: 4, name: 'מלון רמת הגולן', geometry: { lat: 32.9978, lng: 35.7654 }, attributes: { source: 'dev-mock' }, crm: { manager: 'שרה גולן', phone: '053-4445566', email: 'sara@galilee.co.il', status: 'active', rooms: 60, stars: 4, notes: 'הגיש בקשה לתיקון 119' }, layerId: 'hotels', layerDisplayName: 'מלונות' },
  { id: 5, name: 'ספא מצפה הימים', geometry: { lat: 32.9823, lng: 35.5012 }, attributes: { source: 'dev-mock' }, crm: { manager: 'רחל מזרחי', phone: '052-3334455', email: 'rachel@spa.co.il', status: 'active', rooms: 95, stars: 5, notes: 'מוביל תחום wellness' }, layerId: 'hotels', layerDisplayName: 'מלונות' },
  { id: 6, name: 'אכסניית כרמיאל', geometry: { lat: 32.9134, lng: 35.2987 }, attributes: { source: 'dev-mock' }, crm: { manager: 'יוסי פרץ', phone: '058-7778899', email: 'yossi@inn.co.il', status: 'inactive', rooms: 22, stars: 3, notes: 'פרויקט מוקפא' }, layerId: 'hotels', layerDisplayName: 'מלונות' },
  { id: 7, name: 'לודג׳ גבעות הגליל', geometry: { lat: 32.8756, lng: 35.3421 }, attributes: { source: 'dev-mock' }, crm: { manager: 'אורן שמש', phone: '054-6667788', email: 'oren@lodge.co.il', status: 'lead', rooms: 8, stars: 3, notes: 'פנייה ראשונה' }, layerId: 'hotels', layerDisplayName: 'מלונות' },
  { id: 8, name: 'מלון עמק החולה', geometry: { lat: 33.0978, lng: 35.5678 }, attributes: { source: 'dev-mock' }, crm: { manager: 'נועה ברק', phone: '050-9990011', email: 'noa@lodge.co.il', status: 'active', rooms: 35, stars: 4, notes: 'בבדיקת סטטוסים' }, layerId: 'hotels', layerDisplayName: 'מלונות' },
];

export function useGeoLayer(layerId: string): UseGeoLayerResult {
  const [features, setFeatures] = useState<EnrichedFeature[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const layerIdRef = useRef(layerId);
  layerIdRef.current = layerId;
  const requestGenerationRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  const fetchFeatures = useCallback(async (generation: number): Promise<boolean> => {
    const id = layerIdRef.current;
    if (!id) return false;

    try {
      const res = await fetch(
        `/api/geo-layers/${id}/features?t=${Date.now()}`,
        { cache: 'no-store', headers: { Pragma: 'no-cache' } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as EnrichedFeature[];
      if (generation !== requestGenerationRef.current) return false;
      setFeatures(data);
      setError(null);
      hasLoadedOnceRef.current = true;
      return true;
    } catch (err) {
      if (generation !== requestGenerationRef.current) return false;
      if (import.meta.env.DEV) {
        setFeatures(DEV_MOCK_FEATURES);
        setError(null);
        hasLoadedOnceRef.current = true;
        return true;
      }
      setError((err as Error).message);
      return false;
    }
  }, []);

  const runFetch = useCallback(
    (opts?: { showFullPageLoader?: boolean }) => {
      const generation = ++requestGenerationRef.current;
      const showLoader = opts?.showFullPageLoader ?? !hasLoadedOnceRef.current;
      if (showLoader) {
        setLoading(true);
        setError(null);
      }

      void fetchFeatures(generation).finally(() => {
        if (generation === requestGenerationRef.current && showLoader) {
          setLoading(false);
        }
      });
    },
    [fetchFeatures],
  );

  const runFetchRef = useRef(runFetch);
  runFetchRef.current = runFetch;

  /** Initial load + layer switch only (never on background poll). */
  useEffect(() => {
    if (!layerId) return;
    hasLoadedOnceRef.current = false;
    runFetchRef.current({ showFullPageLoader: true });
  }, [layerId]);

  /** Monday-backed layers: silent refresh (entrepreneurs intake + hotels macro board). */
  useEffect(() => {
    const pollMs =
      layerId === 'entrepreneurs'
        ? ENTREPRENEURS_LAYER_POLL_MS
        : layerId === 'agritour-entrepreneurs'
          ? AGRITOUR_ENTREPRENEURS_LAYER_POLL_MS
          : layerId === 'hotels'
            ? HOTELS_LAYER_POLL_MS
            : null;
    if (pollMs == null) return;

    const intervalId = window.setInterval(() => {
      runFetchRef.current({ showFullPageLoader: false });
    }, pollMs);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        runFetchRef.current({ showFullPageLoader: false });
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [layerId]);

  const refetch = useCallback(() => {
    runFetch({ showFullPageLoader: false });
  }, [runFetch]);

  return { features, loading, error, refetch };
}
